import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentLayout from './AgentLayout';
import BoostCheckoutModal from './BoostCheckoutModal';
import ProductCheckoutModal from './ProductCheckoutModal';
import Icon from '../../components/AppIcon';
import { selectCustomerId } from '../../features/auth/authSlice';
import {
  useGetMyAgentPropertiesQuery,
  useDeleteAgentPropertyMutation,
  useGetMonetizationConfigQuery,
  useUpdatePropertyListingMutation,
} from '../../features/api/apiSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';

const listingTypeCode = (p) => {
  const raw = p?.propery_type ?? p?.property_type;
  const n = Number(raw);
  if (n === 0 || n === 1 || n === 2 || n === 3) return n;
  const s = String(raw || '').toLowerCase();
  if (s === 'rent') return 1;
  if (s === 'sold') return 2;
  if (s === 'rented') return 3;
  return 0;
};

const isTakenListing = (p) => {
  if (p?.taken === true || p?.taken === 1 || p?.taken === '1') return true;
  if (p?.taken === false || p?.taken === 0 || p?.taken === '0') return false;
  if (p?.is_premium === true || p?.is_premium === 1 || p?.is_premium === '1') return true;
  const code = listingTypeCode(p);
  return code === 2 || code === 3;
};

const typeLabel = (p) => {
  if (isTakenListing(p)) {
    return listingTypeCode(p) === 3 || String(p?.property_type || '').toLowerCase() === 'rented'
      ? 'Rented'
      : 'Sold';
  }
  const code = listingTypeCode(p);
  if (code === 1) return 'For Rent';
  return 'For Sale';
};

const isBoosted = (p) => {
  if (p?.boosted === true) return true;
  if (!p?.boosted_until) return false;
  const until = new Date(p.boosted_until);
  return !Number.isNaN(until.getTime()) && until.getTime() > Date.now();
};

/** Toggle switch used for Active / Taken columns */
const FlagToggle = ({ checked, onChange, disabled, onLabel, offLabel, accent = 'emerald' }) => {
  const onColors =
    accent === 'amber'
      ? 'bg-amber-500'
      : accent === 'rose'
        ? 'bg-rose-500'
        : 'bg-emerald-500';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-[3.25rem] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 ${
        checked ? onColors : 'bg-slate-300'
      } focus:ring-slate-400`}
      title={checked ? onLabel : offLabel}
    >
      <span
        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

const AgentProperties = () => {
  const customerId = useSelector(selectCustomerId);
  const { formatPrice } = useCurrency();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isFetching, refetch } = useGetMyAgentPropertiesQuery(
    { userid: customerId, limit: 100 },
    { skip: !customerId }
  );
  const [deleteProperty, { isLoading: deleting }] = useDeleteAgentPropertyMutation();
  const { data: monetization } = useGetMonetizationConfigQuery();
  const [updateListing] = useUpdatePropertyListingMutation();
  const [busyId, setBusyId] = useState(null);
  const [boostTarget, setBoostTarget] = useState(null);
  const [tourTarget, setTourTarget] = useState(null);
  const [banner, setBanner] = useState(null);
  const [flagOverrides, setFlagOverrides] = useState({});

  const properties = data?.data || [];

  useEffect(() => {
    const status = params.get('status');
    if (!status) return;
    if (status === 'success') {
      setBanner({ tone: 'ok', text: 'Payment received. Your listing is boosted to the top of search.' });
      refetch();
    } else if (status === 'cancelled') {
      setBanner({ tone: 'warn', text: 'Boost payment was cancelled. You can try again.' });
    } else if (status === 'failed') {
      setBanner({ tone: 'err', text: 'Boost payment did not go through. Try another method.' });
    }
    const next = new URLSearchParams(params);
    next.delete('status');
    setParams(next, { replace: true });
  }, [params, refetch, setParams]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property permanently?')) return;
    try {
      await deleteProperty(id).unwrap();
      refetch();
    } catch (e) {
      setBanner({ tone: 'err', text: e?.data?.message || 'Delete failed' });
    }
  };

  const handleActiveToggle = async (p, next) => {
    setBusyId(p.id);
    setFlagOverrides((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), active: next } }));
    try {
      await updateListing({ property_id: p.id, active: next ? 1 : 0 }).unwrap();
      refetch();
    } catch (e) {
      setFlagOverrides((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), active: undefined } }));
      setBanner({ tone: 'err', text: e?.data?.message || 'Could not update active status' });
    } finally {
      setBusyId(null);
    }
  };

  const handleTakenToggle = async (p, next) => {
    setBusyId(p.id);
    setFlagOverrides((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), taken: next } }));
    try {
      const res = await updateListing({ property_id: p.id, taken: next ? 1 : 0 }).unwrap();
      if (res?.data) {
        setFlagOverrides((prev) => ({
          ...prev,
          [p.id]: {
            taken: !!res.data.taken,
            active: res.data.active,
          },
        }));
      }
      refetch();
    } catch (e) {
      setFlagOverrides((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), taken: undefined } }));
      setBanner({
        tone: 'err',
        text: e?.data?.message || e?.error || 'Could not update taken status. Copy the updated API files to the server.',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AgentLayout
      title="My Properties"
      subtitle="Manage the properties you represent on HireBroker."
    >
      {banner && (
        <div
          className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
            banner.tone === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : banner.tone === 'warn'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
          }`}
        >
          {banner.text}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <p className="text-sm text-slate-600">
          {data?.total_count != null
            ? `${data.total_count} propert${data.total_count === 1 ? 'y' : 'ies'}`
            : ''}
          <span className="text-slate-400"> · </span>
          <span className="text-slate-500">
            Active = live on the market · Taken = sold / rented
          </span>
        </p>
        <Link
          to="/agent-dashboard/properties/new"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
        >
          <Icon name="Plus" size={16} />
          Add Property
        </Link>
      </div>

      {isLoading || isFetching ? (
        <p className="text-slate-500">Loading your portfolio…</p>
      ) : properties.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Icon name="Building2" size={48} className="mx-auto text-slate-300 mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">No properties yet</h2>
          <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
            Publish rentals, sales, and land. They appear on the public market once live.
          </p>
          <Link
            to="/agent-dashboard/properties/new"
            className="inline-flex px-5 py-2.5 rounded-lg bg-amber-500 text-slate-950 font-semibold text-sm"
          >
            Add first property
          </Link>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Property</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3 font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold text-center">Active</th>
                  <th className="px-4 py-3 font-semibold text-center">Taken</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {properties.map((p) => {
                  const override = flagOverrides[p.id] || {};
                  const active =
                    override.active !== undefined ? override.active : Number(p.status) === 1;
                  const taken = override.taken !== undefined ? override.taken : isTakenListing(p);
                  const busy = busyId === p.id;
                  const boosted = isBoosted(p);
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-[200px]">
                          <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                            {p.title_image ? (
                              <img
                                src={p.title_image}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : null}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{p.title}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {p.city || p.address || p.slug_id || `#${p.id}`}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {boosted && (
                                <span className="inline-flex text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                                  Boosted
                                </span>
                              )}
                              {(p.video_tour || p.video_tour_until) && (
                                <span className="inline-flex text-[10px] font-semibold uppercase tracking-wide text-sky-800 bg-sky-100 px-1.5 py-0.5 rounded">
                                  Video tour
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {typeLabel({ ...p, taken })}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">
                        {p.price != null ? formatPrice(p.price, 'UGX') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <FlagToggle
                            checked={active}
                            disabled={busy}
                            onLabel="Active (live)"
                            offLabel="Inactive"
                            accent="emerald"
                            onChange={(v) => handleActiveToggle(p, v)}
                          />
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wide ${
                              active ? 'text-emerald-600' : 'text-slate-400'
                            }`}
                          >
                            {active ? 'On' : 'Off'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <FlagToggle
                            checked={taken}
                            disabled={busy}
                            onLabel="Taken (sold/rented)"
                            offLabel="Available"
                            accent="rose"
                            onChange={(v) => handleTakenToggle(p, v)}
                          />
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wide ${
                              taken ? 'text-rose-600' : 'text-slate-400'
                            }`}
                          >
                            {taken ? 'Taken' : 'Open'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {p.slug_id && (
                          <Link
                            to={`/property/${p.slug_id}`}
                            className="text-xs font-medium text-slate-600 hover:text-slate-900 mr-3"
                          >
                            View
                          </Link>
                        )}
                        {monetization?.boost_enabled && (
                          <button
                            type="button"
                            onClick={() => setBoostTarget(p)}
                            className="text-xs font-medium text-amber-700 hover:text-amber-800 mr-3"
                          >
                            Boost
                          </button>
                        )}
                        {monetization?.video_tour_addon_enabled && (
                          <button
                            type="button"
                            onClick={() => setTourTarget(p)}
                            className="text-xs font-medium text-sky-700 hover:text-sky-800 mr-3"
                          >
                            Video tour
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={deleting}
                          onClick={() => handleDelete(p.id)}
                          className="text-xs font-medium text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {boostTarget && (
        <BoostCheckoutModal
          property={boostTarget}
          monetization={monetization}
          onClose={() => setBoostTarget(null)}
          onPaid={() => {
            setBoostTarget(null);
            setBanner({ tone: 'ok', text: 'Listing boosted to the top of search.' });
            refetch();
          }}
        />
      )}
      {tourTarget && (
        <ProductCheckoutModal
          product="video_tour"
          title="Video / 3D tour"
          description={`Unlock a walkthrough on “${tourTarget.title}”.`}
          fee={monetization?.video_tour_fee_ugx || 0}
          days={monetization?.video_tour_days || 30}
          extraPayload={{ property_id: tourTarget.id }}
          extraFields={[
            { name: 'video_url', label: 'Video URL (YouTube, Vimeo, or MP4)', type: 'url', placeholder: 'https://' },
            { name: 'three_d_url', label: '3D tour URL (optional)', type: 'url', placeholder: 'https://' },
          ]}
          monetization={monetization}
          returnPath="/agent-dashboard/properties"
          onClose={() => setTourTarget(null)}
          onPaid={() => {
            setTourTarget(null);
            setBanner({ tone: 'ok', text: 'Video / 3D tour is unlocked on this listing.' });
            refetch();
          }}
        />
      )}
    </AgentLayout>
  );
};

export default AgentProperties;
